import * as THREE from 'three';
import * as CANNON from 'cannon-es';

const GRAVITY = 26;
const THROW_SPEED = 14;
const FUSE_S = 1.85;
export const CHUG_SPLASH_MAX = 2;
const HEAL_RADIUS = 3.8;
const HEAL_AMOUNT = 22;

type LiveSplash = {
  mesh: THREE.Mesh;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  fuse: number;
  grounded: boolean;
};

type HealFx = {
  mesh: THREE.Mesh;
  t: number;
  maxT: number;
};

export type ChugSplashHeal = {
  x: number;
  y: number;
  z: number;
  radius: number;
  heal: number;
};

/** Throwable area heal — splashes HP to allies in radius on impact. */
export class FpsChugSplash {
  private count = CHUG_SPLASH_MAX;
  private live: LiveSplash[] = [];
  private fx: HealFx[] = [];
  private readonly geo = new THREE.SphereGeometry(0.1, 10, 10);
  private readonly mat = new THREE.MeshStandardMaterial({
    color: 0x6b3fa0,
    emissive: 0x3a1860,
    emissiveIntensity: 0.65,
    roughness: 0.4,
    metalness: 0.2,
  });

  constructor(
    private readonly sceneRoot: THREE.Group,
    private readonly world: CANNON.World,
  ) {}

  getCount(): number {
    return this.count;
  }

  addCount(n: number): number {
    const before = this.count;
    this.count = Math.min(CHUG_SPLASH_MAX, this.count + n);
    return this.count - before;
  }

  tryThrow(origin: THREE.Vector3, dir: THREE.Vector3): boolean {
    if (this.count <= 0) return false;
    this.count -= 1;
    const mesh = new THREE.Mesh(this.geo, this.mat.clone());
    mesh.position.copy(origin);
    this.sceneRoot.add(mesh);
    const vel = dir.clone().normalize().multiplyScalar(THROW_SPEED);
    vel.y += 4.2;
    this.live.push({ mesh, pos: origin.clone(), vel, fuse: FUSE_S, grounded: false });
    return true;
  }

  update(dt: number, onHeal: (h: ChugSplashHeal) => void): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const g = this.live[i]!;
      g.fuse -= dt;
      g.vel.y -= GRAVITY * dt;

      const next = g.pos.clone().add(g.vel.clone().multiplyScalar(dt));
      const from = new CANNON.Vec3(g.pos.x, g.pos.y, g.pos.z);
      const to = new CANNON.Vec3(next.x, next.y, next.z);
      const res = new CANNON.RaycastResult();
      const hit = this.world.raycastClosest(from, to, { skipBackfaces: true }, res);

      if (hit && res.hasHit && res.distance < g.pos.distanceTo(next) + 0.02) {
        g.pos.set(res.hitPointWorld.x, res.hitPointWorld.y + 0.1, res.hitPointWorld.z);
        g.grounded = true;
        g.vel.multiplyScalar(0.35);
      } else {
        g.pos.copy(next);
      }

      g.mesh.position.copy(g.pos);
      g.mesh.rotation.x += dt * 8;
      g.mesh.rotation.z += dt * 6;

      if (g.fuse <= 0 || (g.grounded && g.fuse < FUSE_S - 0.05)) {
        this.pop(g, onHeal);
        this.live.splice(i, 1);
      }
    }

    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i]!;
      f.t += dt;
      const u = Math.min(1, f.t / f.maxT);
      f.mesh.scale.setScalar(0.4 + u * HEAL_RADIUS * 2.2);
      (f.mesh.material as THREE.MeshBasicMaterial).opacity = 0.55 * (1 - u);
      if (f.t >= f.maxT) {
        f.mesh.removeFromParent();
        f.mesh.geometry.dispose();
        (f.mesh.material as THREE.Material).dispose();
        this.fx.splice(i, 1);
      }
    }
  }

  private pop(g: LiveSplash, onHeal: (h: ChugSplashHeal) => void): void {
    g.mesh.removeFromParent();
    (g.mesh.material as THREE.Material).dispose();
    const { x, y, z } = g.pos;
    const ringGeo = new THREE.RingGeometry(0.2, HEAL_RADIUS, 24);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xaf7ac5,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(x, y + 0.08, z);
    this.sceneRoot.add(ring);
    this.fx.push({ mesh: ring, t: 0, maxT: 0.45 });
    onHeal({ x, y, z, radius: HEAL_RADIUS, heal: HEAL_AMOUNT });
  }

  dispose(): void {
    for (const g of this.live) {
      g.mesh.removeFromParent();
      (g.mesh.material as THREE.Material).dispose();
    }
    this.live.length = 0;
    for (const f of this.fx) {
      f.mesh.removeFromParent();
      f.mesh.geometry.dispose();
      (f.mesh.material as THREE.Material).dispose();
    }
    this.fx.length = 0;
    this.geo.dispose();
    this.mat.dispose();
  }
}
